import React from 'react';
import styled from 'styled-components';
import { formatListingMedia, listingMediaIsVideo } from 'utils/listings';
import { ListingV2 } from 'types/listings';
import { useIsMobile } from 'WindowDimensionProvider';
import { BrandFlagContainer } from 'components/molecules/BrandFlagContainer';
import { NormalizedPublicListing } from 'types/listingV3';
import MinimalVideoPlayer from 'components/MinimalVideoPlayer/MinimalVideoPlayer';
import { FeaturedExclusiveStar } from '../../atoms/FeaturedExclusiveStar';
import { FeaturedExclusiveOverlay } from '../../atoms/FeaturedExlusiveOverlay';
import {
  StyledLink,
  VideoContentWrapper,
  WrapperImage,
  WrapperImageRatio,
} from './styles';

/* Fill the fixed 4:3 frame (see WrapperImageRatio) — same width and height as its parent, cropped
   with object-fit: cover so a portrait, landscape, or square source all render at the same tile
   size. `position: absolute` lets us stack the exclusive-star / overlay siblings on top of the
   image inside the same frame. */
const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface FeaturedImageContainerProps {
  exclusiveTag?: string;
  isExclusive: boolean;
  isHovered: boolean;
  title: NormalizedPublicListing['title'];
  medias: ListingV2['medias'];
  brandFlagName?: string;
}

const FeaturedImageContainer = ({
  exclusiveTag,
  isExclusive,
  isHovered,
  title,
  medias,
  brandFlagName,
}: FeaturedImageContainerProps) => {
  const isMobile = useIsMobile();

  const formattedMedias = formatListingMedia(medias).map((media) => ({
    ...media,
    alt: title,
  }));

  const media =
    formattedMedias.find((media) => media.kind === 'cover') ||
    formattedMedias[0];

  if (!media) {
    return null;
  }

  const isVideo = listingMediaIsVideo(media);

  const displayMedia = isMobile ? media?.urlMobile : media?.urlDesktop;

  return (
    <WrapperImage className="featured-experience__item-image-wrapper">
      <WrapperImageRatio
        className="featured-experience__item-image-ratio"
        $isVideo={isVideo}
      >
        {isVideo ? (
          <VideoContentWrapper>
            <StyledLink>
              {isExclusive && isHovered && (
                <FeaturedExclusiveOverlay
                  exclusiveTag={exclusiveTag}
                  isVideo={isVideo}
                />
              )}
              {isExclusive && <FeaturedExclusiveStar />}
              <BrandFlagContainer brandFlagName={brandFlagName}>
                <MinimalVideoPlayer
                  coverUrl={
                    isMobile ? media.previewMobile : media.previewDesktop
                  }
                  fit="cover"
                  key={media.id}
                  loop
                  play={isHovered}
                  videoUrl={isMobile ? media.urlMobile : media.urlDesktop}
                />
              </BrandFlagContainer>
            </StyledLink>
          </VideoContentWrapper>
        ) : (
          <StyledLink>
            {isExclusive && isHovered && (
              <FeaturedExclusiveOverlay
                exclusiveTag={exclusiveTag}
                isVideo={isVideo}
              />
            )}
            {isExclusive && <FeaturedExclusiveStar />}
            <BrandFlagContainer brandFlagName={brandFlagName}>
              <StyledImage
                alt={media.alt ?? undefined}
                className="featured-experience__item-image-responsive-image"
                loading="lazy"
                src={displayMedia}
              />
            </BrandFlagContainer>
          </StyledLink>
        )}
      </WrapperImageRatio>
    </WrapperImage>
  );
};

export default FeaturedImageContainer;
